import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a", amberText: "#92400e",
  blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:     <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:    <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    ChevronRight:  <><path d="m9 18 6-6-6-6"/></>,
    Search:        <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    MapPin:        <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Star:          <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    BadgeCheck:    <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Calendar:      <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    Clock:         <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    Wallet:        <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    ShieldCheck:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    Zap:           <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Plug:          <><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a2 2 0 0 0-2 2v2a7 7 0 1 0 14 0v-2a2 2 0 0 0-2-2Z"/></>,
    AlertTriangle: <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"/><path d="M12 9v4M12 17h.01"/></>,
    Lightbulb:     <><path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.8.8 1.21 1.5 1.41 2.5"/></>,
    Camera:        <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></>,
    Home:          <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    User:          <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const SUBSERVICES = [
  { icon: "Plug",      label: "Socket & switch repair",  range: "KES 800 – 3,000",   note: "per point" },
  { icon: "Zap",       label: "Wiring & rewiring",        range: "KES 3,000 – 20,000", note: "per room/property" },
  { icon: "Lightbulb", label: "Lighting installation",   range: "KES 1,000 – 6,000",  note: "per fixture" },
  { icon: "Camera",    label: "Security lighting & CCTV", range: "KES 5,000 – 25,000", note: "per installation" },
  { icon: "Home",      label: "Consumer unit / DB board", range: "KES 4,000 – 15,000", note: "per job" },
  { icon: "AlertTriangle", label: "Fault finding & diagnosis", range: "KES 1,500 – 5,000", note: "per visit" },
];

const HOW_IT_WORKS = [
  { num: "01", icon: "Search",   title: "Describe the issue",        desc: "Tell us what's happening — tripping breaker, no power, an installation — and your location." },
  { num: "02", icon: "User",     title: "Compare verified electricians", desc: "See ratings, reviews, pricing, and real availability before you choose who comes to your home." },
  { num: "03", icon: "Calendar", title: "Book a time that works",    desc: "Same-day slots are available for urgent jobs. Confirm the time and any access details in-app." },
  { num: "04", icon: "Wallet",   title: "Pay only once it's fixed",  desc: "Funds are held securely and released to the electrician only after you confirm the job is done." },
];

const WHY_POINTS = [
  { icon: "BadgeCheck",  title: "Every electrician is verified",  desc: "ID-checked and background-reviewed before they ever appear in search results. No exceptions." },
  { icon: "Star",        title: "Reviews tied to real bookings",   desc: "Every rating comes from a completed, paid booking. No anonymous or fake reviews ever make it onto a profile." },
  { icon: "ShieldCheck", title: "Covered by the Jirani Guarantee", desc: "If wiring work fails or is incomplete, our Trust and Safety team reviews it, including refunds where warranted." },
  { icon: "Wallet",      title: "Pay only when it's fixed",        desc: "Funds are held securely and only released to the electrician after you confirm the work meets what was agreed." },
];

const EMERGENCY_SIGNS = [
  "A burning smell, smoke, or visible scorch marks near a socket or switch",
  "A socket or switch that feels warm or hot to the touch",
  "Sparking from an outlet, plug, or the consumer unit",
  "A circuit breaker that keeps tripping repeatedly with no obvious cause",
  "Exposed or damaged wiring anywhere in the property",
];

const FAQS = [
  {
    q: "How much does an electrician cost in Kenya through JiraniApp?",
    a: "Smaller jobs like socket repairs or single light fittings typically range from KES 800 to 3,000. Larger work such as rewiring a room, installing a consumer unit, or fitting a full security lighting system can run from KES 4,000 to 25,000 or more depending on scope and materials. Every JiraniPro lists their own rates on their profile, and many offer a free or low-cost assessment for larger jobs before confirming a final price.",
  },
  {
    q: "Can I get an electrician the same day for an urgent issue?",
    a: "Yes. Many electricians on JiraniApp list same-day availability, and you can filter search results specifically by availability today. For genuine electrical emergencies — burning smells, sparking, exposed wiring — switch off the affected circuit at the consumer unit if it is safe to do so, then book the nearest available verified electrician and flag the job as urgent.",
  },
  {
    q: "Do I need to supply materials such as wiring, sockets, or fittings myself?",
    a: "This varies by electrician and is usually noted on their profile. Most electricians bring standard parts, cabling, and fittings and include materials in their final invoice. For larger jobs like a full rewire or a specific light fixture you want installed, agree on who is sourcing materials before the job begins.",
  },
  {
    q: "Is it safe to keep using a socket or switch that has tripped the breaker repeatedly?",
    a: "No. A circuit breaker that trips more than once is signalling a fault, not a one-off surge. Continuing to reset it and use the same circuit risks overheating and fire. Stop using that circuit, and book a qualified electrician to diagnose the fault rather than resetting the breaker repeatedly.",
  },
  {
    q: "What happens if the same electrical problem comes back after the repair?",
    a: "Message the electrician directly through the app first — most will return to address a recurring issue at no extra charge if it is within a reasonable period after the original job. If the electrician is unresponsive or the issue is not resolved, you can open a dispute through Help and Support within 48 hours of the original booking for a fair review.",
  },
  {
    q: "Do JiraniApp electricians handle commercial or office electrical work?",
    a: "Yes, many electricians on the platform list commercial experience on their profile alongside residential work. For office fit-outs, consumer unit upgrades for higher loads, or building-wide rewiring, look for electricians who specifically mention commercial or industrial experience, and request a site visit before any large quote is finalised.",
  },
];

const TESTIMONIALS = [
  { name: "Brian K.",   area: "Karen, Nairobi",      text: "My consumer unit kept tripping every evening. The electrician traced it to a faulty circuit in under an hour and fixed it properly the same visit.", rating: 5 },
  { name: "Halima J.",  area: "Bamburi, Mombasa",    text: "Had security lighting and CCTV installed around the compound. Clean wiring, no mess left behind, and it has worked flawlessly since.", rating: 5 },
  { name: "Peter W.",   area: "Kilimani, Nairobi",   text: "Full rewire of an old two-bedroom unit before we moved in. The electrician explained every stage and the final invoice matched the quote exactly.", rating: 5 },
];

/* ─────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────── */

function SubServiceCard({ icon, label, range, note }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "20px 18px", borderRadius: 14,
        border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: hovered ? T.g50 : T.white,
        transition: "all .18s", cursor: "default",
      }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: hovered ? T.g100 : T.n50,
        border: `1px solid ${hovered ? T.g200 : T.n150}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 14, transition: "all .18s",
      }}>
        <Icon name={icon} size={17} color={hovered ? T.g600 : T.n400} strokeWidth={1.7} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, marginBottom: 7 }}>{label}</div>
      <div style={{ fontFamily: T.serif, fontSize: 19, fontWeight: 400, color: hovered ? T.g700 : T.n900, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{range}</div>
      <div style={{ fontSize: 11, color: T.n400, marginTop: 3 }}>{note}</div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", textAlign: "left", background: "none", border: "none",
        padding: "22px 0", display: "flex", justifyContent: "space-between",
        alignItems: "center", cursor: "pointer", gap: 20, fontFamily: T.sans,
      }}>
        <span style={{ fontSize: 15, fontWeight: 400, color: T.n800, lineHeight: 1.5, letterSpacing: "-0.01em" }}>{q}</span>
        <span style={{
          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
          border: `1px solid ${open ? T.g500 : T.n200}`,
          background: open ? T.g500 : "transparent",
          color: open ? T.white : T.n400,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .22s cubic-bezier(.4,0,.2,1)",
          transform: open ? "rotate(45deg)" : "none",
          fontSize: 18, fontWeight: 300, lineHeight: 1,
        }}>+</span>
      </button>
      <div style={{ overflow: "hidden", maxHeight: open ? 400 : 0, transition: "max-height .32s cubic-bezier(.4,0,.2,1)" }}>
        <p style={{ margin: "0 0 22px", fontSize: 15, color: T.n600, lineHeight: 1.75, paddingRight: 48, fontFamily: T.sans }}>{a}</p>
      </div>
    </div>
  );
}

function TestimonialCard({ name, area, text, rating }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("");
  return (
    <div style={{ padding: "22px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Icon key={i} name="Star" size={13} color={i < rating ? T.amber : T.n150} strokeWidth={0} />
        ))}
      </div>
      <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.75, margin: 0 }}>"{text}"</p>
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 6, borderTop: `1px solid ${T.n100}` }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: T.g700, fontFamily: T.sans }}>{initials}</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.n900 }}>{name}</div>
          <div style={{ fontSize: 11, color: T.n400 }}>{area}</div>
        </div>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function ElectricalCategoryPage() {
  useFonts();
  const navigate = useNavigate();
  const [location, setLocation] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const goToSearch = () => {
    const params = location.trim() ? `?category=electrical&location=${encodeURIComponent(location.trim())}` : "?category=electrical";
    navigate(`/search${params}`);
  };

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900, paddingBottom: 60 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "none", border: "none", cursor: "pointer",
          color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={() => navigate("/pro-onboarding")} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "none", border: `1px solid ${T.n200}`, cursor: "pointer",
          color: T.n600, fontSize: 13, fontFamily: T.sans,
          padding: "8px 16px", borderRadius: 8, fontWeight: 500,
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n900; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n600; }}>
          Are you an electrician? Join as a Pro
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(52px,7vw,88px) 20px clamp(40px,5vw,56px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 22 }}>
            <Icon name="Zap" size={13} color={T.g500} strokeWidth={2.2} />
            Electrical Services
          </div>
          <h1 style={{
            fontFamily: T.serif, fontSize: "clamp(38px,7vw,64px)", fontWeight: 400,
            margin: "0 0 18px", lineHeight: 1.07, color: T.n900, letterSpacing: "-0.03em", maxWidth: 600,
          }}>
            Wired right,<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>by people who do it safely</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 32px", maxWidth: 520 }}>
            Book verified, reviewed electricians across Kenya for repairs, installations, and faults.
            Compare prices, check real availability, and get same-day help when something is not safe to wait on.
          </p>

          {/* Search bar */}
          <div style={{
            display: "flex", gap: 8, padding: 8, borderRadius: 14,
            background: T.n50, border: `1.5px solid ${searchFocused ? T.g400 : T.n150}`,
            boxShadow: searchFocused ? `0 0 0 4px ${T.g100}` : "0 1px 4px rgba(0,0,0,.04)",
            transition: "all .18s", flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: 180, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: T.white, borderRadius: 10 }}>
              <Icon name="MapPin" size={16} color={T.n400} strokeWidth={1.8} />
              <input
                value={location}
                onChange={e => setLocation(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Your area, e.g. Karen, Bamburi..."
                style={{ flex: 1, border: "none", outline: "none", fontSize: 14, fontFamily: T.sans, color: T.n900, background: "transparent" }}
              />
            </div>
            <button onClick={goToSearch} style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "12px 22px", background: T.g500, color: T.white,
              border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans, whiteSpace: "nowrap",
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              <Icon name="Search" size={15} color={T.white} strokeWidth={2} />
              Find Pros
            </button>
          </div>
        </div>
      </div>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}`, padding: "0 20px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
          {[
            { icon: "BadgeCheck", label: "100+ verified electricians" },
            { icon: "Star",       label: "4.7 average rating across bookings" },
            { icon: "Clock",      label: "Same-day slots available for urgent jobs" },
          ].map(({ icon, label }, i) => (
            <div key={i} style={{ padding: "20px", display: "flex", alignItems: "center", gap: 11, borderRight: i < 2 ? `1px solid ${T.n100}` : "none" }}>
              <Icon name={icon} size={17} color={T.g500} strokeWidth={1.6} />
              <span style={{ fontSize: 13, color: T.n600, lineHeight: 1.4 }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>

        {/* ══ EMERGENCY CALLOUT ══ */}
        <div style={{
          marginTop: "clamp(32px,5vw,48px)",
          padding: "20px 22px", borderRadius: 16,
          background: T.amberBg, border: `1.5px solid ${T.amberBorder}`,
          display: "flex", gap: 14, alignItems: "flex-start",
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: T.white, border: `1px solid ${T.amberBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="AlertTriangle" size={17} color={T.amber} strokeWidth={1.8} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.amberText, marginBottom: 6 }}>
              Is this an electrical emergency?
            </div>
            <p style={{ fontSize: 13, color: T.amberText, lineHeight: 1.7, margin: "0 0 10px" }}>
              If you notice any of the following, stop using the affected circuit immediately, switch it off at the consumer unit if it is safe to do so, then book the nearest available verified electrician and flag the job as urgent:
            </p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {EMERGENCY_SIGNS.map((sign, i) => (
                <li key={i} style={{ fontSize: 13, color: T.amberText, lineHeight: 1.7, marginBottom: 4 }}>{sign}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* ══ SUB-SERVICES ══ */}
        <div style={{ paddingTop: "clamp(48px,7vw,72px)", marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>What's included</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Every electrical job, <em style={{ fontStyle: "italic", color: T.g600 }}>covered</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            From a single dead socket to a full property rewire. Prices reflect what Pros on the platform are currently charging.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12 }}>
            {SUBSERVICES.map((s, i) => <SubServiceCard key={i} {...s} />)}
          </div>
        </div>

        {/* ══ HOW IT WORKS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>How it works</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Booked in minutes, <em style={{ fontStyle: "italic", color: T.g600 }}>fixed properly</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 28px", lineHeight: 1.65 }}>
            No phone calls, no back-and-forth. Just clear pricing and verified people.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
            {HOW_IT_WORKS.map((step, i) => (
              <div key={i} style={{ padding: "22px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.n50, display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: T.white, border: `1px solid ${T.n150}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon name={step.icon} size={17} color={T.g500} strokeWidth={1.6} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.08em", marginBottom: 4 }}>STEP {step.num}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n900, marginBottom: 6 }}>{step.title}</div>
                  <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.65, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <button onClick={goToSearch} style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: T.g500, color: T.white, border: "none",
              padding: "13px 28px", borderRadius: 11, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: T.sans,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Browse electricians near you
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* ══ WHY JIRANIAPP ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Why book through JiraniApp</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 24px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Trust, built into <em style={{ fontStyle: "italic", color: T.g600 }}>every booking</em>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
            {WHY_POINTS.map((item, i) => (
              <div key={i} style={{ padding: "22px", borderRadius: 14, border: `1px solid ${T.n150}`, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: T.g50, border: `1px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={item.icon} size={17} color={T.g500} strokeWidth={1.6} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n900 }}>{item.title}</div>
                <p style={{ fontSize: 13, color: T.n600, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ TESTIMONIALS ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>From clients</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 24px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            What people are <em style={{ fontStyle: "italic", color: T.g600 }}>saying</em>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} {...t} />)}
          </div>
        </div>

        {/* ══ FAQ ══ */}
        <div style={{ marginBottom: "clamp(48px,7vw,72px)" }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 12px" }}>Common questions</p>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,36px)", fontWeight: 400, color: T.n900, margin: "0 0 4px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Before you <em style={{ fontStyle: "italic", color: T.g600 }}>book</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 0 8px", lineHeight: 1.65 }}>
            Everything clients usually ask about electrical bookings.
          </p>
          <div>
            {FAQS.map((item, i) => <FaqItem key={i} {...item} />)}
          </div>
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{ padding: "clamp(36px,5vw,56px)", borderRadius: 20, border: `1px solid ${T.n150}`, textAlign: "center", background: T.n50 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: T.g50, border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Icon name="Zap" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{ fontFamily: T.serif, fontSize: "clamp(28px,4.5vw,40px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            Something not working <em style={{ fontStyle: "italic", color: T.g600 }}>right?</em>
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 32px", lineHeight: 1.75, maxWidth: 400 }}>
            Compare verified electricians near you and book in minutes.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={goToSearch} style={{
              padding: "14px 32px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Find an electrician
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/how-it-works")} style={{
              padding: "14px 28px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 15, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Learn how JiraniApp works
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}